import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Sparkles, RefreshCw, AlertTriangle, TrendingUp, TrendingDown,
    Award, Activity, ChevronRight, Target,
} from "lucide-react";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
    Area, AreaChart, CartesianGrid,
} from "recharts";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { StatCard } from "@/components/ui/StatCard";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet, apiPost } from "@/lib/api";
import { formatAOA } from "@/lib/utils";
import pt from "@/lib/i18n";

const COPPER = "var(--copper)";
const COPPER_LIGHT = "var(--copper-light)";

const _fmtMonthDay = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "short" });
    } catch (e) { return iso; }
};

const TrendChart = ({ title, data, dataKey, color, format = "number", testId }) => {
    const safe = data || [];
    return (
        <GlassCard padding="p-5" data-testid={testId}>
            <p className="font-body text-xs uppercase tracking-[0.16em] text-[color:var(--slate-mid)] mb-3">{title}</p>
            <div style={{ width: "100%", height: 180 }}>
                <ResponsiveContainer>
                    <AreaChart data={safe.map(d => ({ ...d, label: _fmtMonthDay(d.snapshot_date) }))}>
                        <defs>
                            <linearGradient id={`grad-${testId}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={color} stopOpacity={0.42} />
                                <stop offset="100%" stopColor={color} stopOpacity={0.0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid stroke="rgba(95,107,126,0.06)" vertical={false} />
                        <XAxis dataKey="label" tick={{ fill: "var(--slate-mid)", fontSize: 9 }} stroke="rgba(95,107,126,0.15)" tickLine={false} interval="preserveStartEnd" minTickGap={40} />
                        <YAxis tick={{ fill: "var(--slate-mid)", fontSize: 10 }} stroke="rgba(95,107,126,0.15)" tickLine={false} axisLine={false} width={42} />
                        <Tooltip
                            contentStyle={{ background: "rgba(255,247,237,0.96)", border: `1px solid ${color}55`, borderRadius: 8, fontFamily: "DM Sans, sans-serif", fontSize: 12 }}
                            labelStyle={{ color: "var(--slate-deep)", fontWeight: 600 }}
                            formatter={(v) => format === "percent" ? [`${v}%`, ""] : [Number(v).toLocaleString("pt-PT"), ""]}
                        />
                        <Area
                            type="monotone"
                            dataKey={dataKey}
                            stroke={color}
                            strokeWidth={2}
                            fill={`url(#grad-${testId})`}
                            isAnimationActive
                            animationDuration={1200}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </GlassCard>
    );
};

const AnalyticsPage = () => {
    const [kpis, setKpis] = useState(null);
    const [trends, setTrends] = useState(null);
    const [summary, setSummary] = useState(null);
    const [period, setPeriod] = useState("week");
    const [generating, setGenerating] = useState(false);
    const [displaySummary, setDisplaySummary] = useState("");
    const loadAll = async () => {
        try {
            const [k, t, s] = await Promise.all([
                apiGet("/analytics/kpis"),
                apiGet("/analytics/trends?days=180"),
                apiGet("/executive-summary/latest"),
            ]);
            setKpis(k);
            setTrends(t || []);
            setSummary(s);
            setDisplaySummary(s?.summary_text || "");
        } catch (e) {}
    };

    useEffect(() => { loadAll(); }, []);

    const typewriterRef = React.useRef(null);

    const _typewrite = (text) => {
        if (typewriterRef.current) clearInterval(typewriterRef.current);
        setDisplaySummary("");
        let i = 0;
        typewriterRef.current = setInterval(() => {
            i += 8;
            if (i >= text.length) {
                setDisplaySummary(text);
                clearInterval(typewriterRef.current);
                typewriterRef.current = null;
            } else {
                setDisplaySummary(text.slice(0, i));
            }
        }, 14);
    };

    useEffect(() => () => {
        if (typewriterRef.current) clearInterval(typewriterRef.current);
    }, []);

    const regenerate = async () => {
        setGenerating(true);
        setDisplaySummary("");
        try {
            const r = await apiPost("/executive-summary/generate", { period });
            setSummary(r);
            setGenerating(false);
            _typewrite(r.summary_text || "");
        } catch (e) {
            setGenerating(false);
        }
    };

    return (
        <div data-testid="analytics-page" className="space-y-6">
            <PageHeader
                title={pt.analytics.title}
                subtitle={pt.analytics.subtitle}
                testId="analytics-header"
                right={
                    <select
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="px-3 py-1.5 rounded-md bg-white/80 border border-[color:var(--glass-border)] text-sm font-body text-[color:var(--slate-deep)]"
                        data-testid="period-select"
                    >
                        <option value="day">{pt.analytics.periodDay}</option>
                        <option value="week">{pt.analytics.periodWeek}</option>
                        <option value="month">{pt.analytics.periodMonth}</option>
                    </select>
                }
            />

            {/* Executive Summary HERO */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                <GlassCard
                    padding="p-6 lg:p-8"
                    data-testid="exec-summary-card"
                    style={{
                        borderColor: "var(--copper)",
                        borderWidth: 1.5,
                        background: "linear-gradient(135deg, rgba(255,247,237,0.96) 0%, rgba(248,235,221,0.85) 100%)",
                    }}
                >
                    <div className="flex flex-col lg:flex-row gap-6">
                        <div className="lg:w-3/4">
                            <div className="flex items-center gap-2 mb-3">
                                <Sparkles size={18} className="text-[color:var(--copper)]" />
                                <span className="font-body text-xs uppercase tracking-[0.2em] text-[color:var(--copper-dark)] font-semibold">
                                    {pt.analytics.execSummary}
                                </span>
                                <span className="font-mono text-[10px] text-[color:var(--slate-mid)] ml-auto">
                                    {summary?.generated_at ? `Gerado a ${new Date(summary.generated_at).toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                                </span>
                            </div>

                            {summary?.headline && (
                                <motion.h1
                                    key={summary?.id || "headline"}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="font-display text-2xl lg:text-3xl text-[color:var(--slate-deep)] leading-tight tracking-tight mb-4"
                                    data-testid="exec-headline"
                                >
                                    {summary.headline}
                                </motion.h1>
                            )}

                            {generating && !displaySummary ? (
                                <div className="space-y-2">
                                    <motion.div className="flex items-center gap-2 text-[color:var(--copper-dark)] font-body text-sm italic"
                                        animate={{ opacity: [0.6, 1, 0.6] }}
                                        transition={{ duration: 1.5, repeat: Infinity }}
                                    >
                                        <Sparkles size={14} /> {pt.analytics.generating}
                                    </motion.div>
                                    <Skeleton className="h-4" />
                                    <Skeleton className="h-4 w-11/12" />
                                    <Skeleton className="h-4 w-10/12" />
                                </div>
                            ) : displaySummary ? (
                                <div
                                    className="font-body text-[15px] text-[color:var(--slate-deep)] leading-[1.75] whitespace-pre-wrap"
                                    data-testid="exec-summary-text"
                                >
                                    {displaySummary}
                                    {generating && <span className="inline-block w-1 h-4 bg-[color:var(--copper)] ml-0.5 animate-pulse" />}
                                </div>
                            ) : (
                                <p className="font-body text-sm text-[color:var(--slate-mid)] italic">{pt.analytics.execSummaryEmpty}</p>
                            )}

                            {summary?.highlights?.length > 0 && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
                                    <div>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[color:var(--success)] mb-2 flex items-center gap-1">
                                            <Award size={11} /> {pt.analytics.highlights}
                                        </p>
                                        <ul className="space-y-1.5">
                                            {summary.highlights.map((h, i) => (
                                                <li key={i} className="font-body text-sm text-[color:var(--slate-deep)] flex items-start gap-1.5">
                                                    <ChevronRight size={13} className="mt-1 text-[color:var(--success)] shrink-0" />
                                                    <span>{h}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    {summary.risks?.length > 0 && (
                                        <div>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[color:var(--error)] mb-2 flex items-center gap-1">
                                                <AlertTriangle size={11} /> {pt.analytics.risks}
                                            </p>
                                            <ul className="space-y-1.5">
                                                {summary.risks.map((r, i) => (
                                                    <li key={i} className="font-body text-sm text-[color:var(--slate-deep)] flex items-start gap-1.5">
                                                        <ChevronRight size={13} className="mt-1 text-[color:var(--error)] shrink-0" />
                                                        <span>{r}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="lg:w-1/4 lg:border-l lg:pl-6 border-[color:var(--copper-subtle)] flex flex-col gap-3">
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[color:var(--slate-mid)] mb-1.5">{pt.analytics.keyMetrics}</p>
                                {summary?.key_metrics && (
                                    <div className="space-y-2">
                                        <div className="flex justify-between font-body text-sm">
                                            <span className="text-[color:var(--slate-mid)]">{pt.common.chartActivations}</span>
                                            <span className="text-[color:var(--slate-deep)] font-medium">{summary.key_metrics.activations}</span>
                                        </div>
                                        <div className="flex justify-between font-body text-sm">
                                            <span className="text-[color:var(--slate-mid)]">Tickets resolvidos</span>
                                            <span className="text-[color:var(--slate-deep)] font-medium">{summary.key_metrics.tickets_resolved}/{summary.key_metrics.tickets_opened}</span>
                                        </div>
                                        <div className="flex justify-between font-body text-sm">
                                            <span className="text-[color:var(--slate-mid)]">{pt.common.chartCsat}</span>
                                            <span className="text-[color:var(--slate-deep)] font-medium">{summary.key_metrics.avg_csat}/5</span>
                                        </div>
                                        <div className="flex justify-between font-body text-sm">
                                            <span className="text-[color:var(--slate-mid)]">SLA campo</span>
                                            <span className="text-[color:var(--slate-deep)] font-medium">{summary.key_metrics.avg_sla_pct}%</span>
                                        </div>
                                        <div className="flex justify-between font-body text-sm">
                                            <span className="text-[color:var(--slate-mid)]">Novos clientes</span>
                                            <span className="text-[color:var(--slate-deep)] font-medium">+{summary.key_metrics.new_customers}</span>
                                        </div>
                                        <div className="flex justify-between font-body text-sm">
                                            <span className="text-[color:var(--slate-mid)]">Receita em risco</span>
                                            <span className="text-[color:var(--error)] font-medium">AOA {formatAOA(summary.key_metrics.revenue_at_risk_aoa)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <NxButton
                                variant="copper"
                                onClick={regenerate}
                                disabled={generating}
                                className="w-full"
                                icon={<RefreshCw size={14} className={generating ? "animate-spin" : ""} />}
                                data-testid="btn-refresh-summary"
                            >
                                {generating ? "A gerar…" : pt.analytics.refresh}
                            </NxButton>
                        </div>
                    </div>
                </GlassCard>
            </motion.div>

            {/* 4 trend charts */}
            <div>
                <p className="font-body text-xs uppercase tracking-[0.16em] text-[color:var(--slate-mid)] mb-3">{pt.analytics.trends}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TrendChart
                        title={pt.analytics.chartActivations}
                        data={trends}
                        dataKey="activations_count"
                        color="#C87941"
                        testId="chart-activations"
                    />
                    <TrendChart
                        title={pt.analytics.chartCSAT}
                        data={trends}
                        dataKey="avg_csat"
                        color="#2D7A4F"
                        testId="chart-csat"
                    />
                    <TrendChart
                        title={pt.analytics.chartSLA}
                        data={trends}
                        dataKey="field_sla_compliance_pct"
                        color="#2D6A8A"
                        format="percent"
                        testId="chart-sla"
                    />
                    <TrendChart
                        title={pt.analytics.chartPipeline}
                        data={trends}
                        dataKey="b2b_pipeline_value_usd"
                        color="#7A5A9C"
                        testId="chart-pipeline"
                    />
                </div>
            </div>
        </div>
    );
};

export default AnalyticsPage;
